import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useFragment, useRelayEnvironment } from 'react-relay';

import { getObjectFromRelayStoreById, hashString } from '../functions';
import { PageInfoFragment } from '../graphql/fragments/PageInfoFragment';
import useGraphQLLoader, { IGraphQLLoader } from '../hooks/useGraphQLLoader';
import { store } from '../store';
import { setterSetting } from '../store/slices/settingSlice';

const DataLoader = <T extends {}, E>({
    query,
    fragmentList,
    fragment,
    fragmentConnection,
    nameQuery,
    varRequest,
    onUpdateData,
    prefixFetchKey,
    fetchPolicy,
    externalFetchKey,
}: IGraphQLLoader<T, E>) => {
    const environment = useRelayEnvironment();
    const dataRowsRef = useRef<any[]>([]);
    const [hashDataRows, setHashDataRows] = useState<string>('');
    const dispatch = useDispatch();

    const paramsLoader: IGraphQLLoader<T, E> = {
        varRequest: varRequest || ({} as T),
        query,
        nameQuery,
        fragmentList,
        fragment,
        fragmentConnection,
        prefixFetchKey,
        fetchPolicy,
        needStructure: false,
    };

    const { connectionId, dataConnection } = useGraphQLLoader(paramsLoader);

    const dataPageInfo = useFragment<any>(PageInfoFragment, dataConnection.pageInfo);

    const update = () => {
        const stateFetchPolicy = store.getState().setting.fetchPolicy;
        if (stateFetchPolicy === 'network-only') {
            setHashDataRows(new Date().getTime().toString());
            dispatch(setterSetting({ fetchPolicy: null }));
        } else {
            setHashDataRows(hashString(JSON.stringify(dataRowsRef.current)));
        }
    };

    useEffect(() => {
        if (hashDataRows) {
            onUpdateData?.(dataRowsRef.current as any, dataPageInfo.count, connectionId);
        }
    }, [hashDataRows]);

    useEffect(() => {
        dataRowsRef.current = dataConnection.edges.map((edge) => ({
            ...getObjectFromRelayStoreById<{ id: string; key: string }>(environment, edge.node.__id),
            key: edge.node.__id,
        }));

        update();
    }, [dataConnection, externalFetchKey]);

    return null;
};

export default DataLoader;
